import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {InventoryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import {ELN_ANALYTICS_ACTIONS, LEGACY_WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SampleConstants from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import getNoteLinkedObjectsOfTypes from 'notebook/notes/util/getNoteLinkedObjectsOfTypes';
import makeEmptySampleDetails from 'notebook/sample-propagation/util/makeEmptySampleDetails';
import * as StoreTransformUtil from 'notebook/store/StoreTransformUtil';
import {
  ColumnDefinition,
  ColumnGroup,
  getColumnDefinitionIndex,
  getColumnLabel,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import toColumnDefinition from 'notebook/structured-tables/columns/toColumnDefinition';
import {getSpreadsheetTypeForColumn} from 'notebook/structured-tables/spreadsheet-import/getSpreadsheetColumnTypes';
import {StructuredTableColumn} from 'notebook/structured-tables/StructuredTableConstants';
import {
  makeSchemaFieldStructuredColumn,
  makeStructuredTableColumn,
} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {prepareLegacyColumnForSave} from 'notebook/structured-tables/util/StructuredTableStoreTransforms';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Cells} from 'notebook/tables/constants/RowConstants';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  TableColumnType,
} from 'notebook/tables/constants/TableColumnConstants';
import {Table, TableData} from 'notebook/tables/constants/TableConstants';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import excess from 'notebook/tables/excess';
import {TableOpOutput} from 'notebook/tables/excess/ops/TableOps';
import {TokenUpdater} from 'notebook/tables/excess/ops/TokenUpdater';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {StorablesTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import {
  getDefaultCell,
  getSourceColumnCellFromSampleDetails,
  getSourceSampleStructuredTableColumns,
  SourceSampleType,
} from 'notebook/transfer-tables/TransferTableColumnDefinitions';
import {
  FromServerTransferTableConfig,
  FromServerTransferTableModel,
  ToServerTransferTableConfig,
  ToServerTransferTableModel,
  TransferOperation,
  TransferTableColumnGroup,
  TransferTableConfig,
  TransferTableModel,
  TransferTableRow,
} from 'notebook/transfer-tables/TransferTableConstants';
import {Note, NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {makeSampleDetailsFromSamples} from 'samples/StageRunSampleModelManager';
import {nullableRESTStorableSummaryToGraphQL} from 'samples/storables/graphql/translateStorable';
import {ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import APIUtil from 'util/APIUtil';
import {SampleDetailsWithOptionalStageRunSample} from 'workflows/util/StageRunUtil';
import {StageRun} from 'workflows/WorkflowConstants';

export interface ImportedTransferTableRow {
  stageRunId?: string | null; // deprecated and unused
  stageRunSampleId?: string | null; // deprecated and unused
  nonSchemaFieldCellsByColumnType: {[columnType: string]: string};
  schemaFieldCellsByFieldId: {[schemaFieldId: string]: string};
  // Required for validating imported rows
  samplesForSampleDetails: {
    batch: SampleConstants.Batch | null;
    bioentitySummary: BioentitySummary | null;
    container: SampleConstants.Container | null;
    plate: SampleConstants.StorableSummary | null;
  };
}

/**
 * @param fileId API ID of the file
 * @param isConfig Boolean indicating if this file is a template/config or executable
 * @returns Returns a boolean indicating whether the transfer table is supported for the given file
 */
export function isTransferTableSupportedForFile(fileId: string, isConfig: boolean = true): boolean {
  if (APIUtil.isDocument(fileId) && isConfig) {
    return false;
  }
  if (APIUtil.isBaseEntry(fileId)) {
    return isFeatureEnabled({feature: InventoryFeature.INVENTORY_EDIT});
  }
  return false;
}

/**
 * @param fileId API ID of the file
 * @param isConfig Boolean indicating if this file is a template/config or executable
 * @returns Returns a boolean indicating whether the config transfer table is supported for the given file
 */
export function isTransferTableConfigSupportedForFile(fileId: string, isConfig: boolean = false): boolean {
  if (APIUtil.isSubtemplate(fileId) && !BenchlingConfig.ELN_ENABLE_INSERT_NEW_TRANSFER_TABLE) {
    // Sub-Templates do not support legacy tables, so don't show it if we are not inserting new transfer or inventory tables
    return false;
  } else if (APIUtil.isEntryOrSubOrWorkflowTemplate(fileId) || (APIUtil.isDocument(fileId) && isConfig)) {
    return isFeatureEnabled({feature: InventoryFeature.INVENTORY_EDIT});
  }
  return false;
}

export function createTransferTableConfig({
  name,
  containerSchema,
  parentStorableIsGrid,
  selectorsById,
  fileId,
}: {
  name: string;
  containerSchema: ContainerSchema;
  parentStorableIsGrid: boolean;
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
  fileId: string;
}): TransferTableConfig {
  if (APIUtil.isStageEntryTemplate(fileId)) {
    analytics.deprecatedTrackEvent(LEGACY_WORKFLOWS_ANALYTICS_ACTIONS.CREATED_TRANSFER_TABLE_CONFIG);
  } else {
    analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.CREATED_TRANSFER_TABLE_CONFIG_IN_NOTEBOOK_TEMPLATE);
  }
  return {
    cid: _.uniqueId('trftcfg'),
    type: NoteLinkedObjectType.TRANSFER_TABLE_CONFIG,
    name,
    columns: getDefaultStructuredTableColumns({containerSchema, parentStorableIsGrid, selectorsById, fileId}),
    containerSchema,
    parentStorableIsGrid,
    provenanceUuid: uuidv4(),
    containerSchemaId: containerSchema.id,
  };
}

function getConfigLockedCellProperty(columnDefinition: ColumnDefinition | null): Partial<Cell> {
  return columnDefinition == null
    ? {
        locked: true,
      }
    : getLockedCellProperty(columnDefinition);
}

export function createTableForConfig(
  name: string,
  structuredTableColumns: Array<StructuredTableColumn>,
  containerSchema: ContainerSchema | null,
  parentStorableIsGrid: boolean,
  provenanceUuid?: string | null
): Table {
  let columnDefinitions: Array<ColumnDefinition> = [];
  if (containerSchema) {
    columnDefinitions = structuredTableColumns.map((structuredTableColumn) =>
      structuredTableColumnToColumnDefinition(
        structuredTableColumn,
        containerSchema,
        parentStorableIsGrid,
        [], // stageRuns
        true // should always be able to change units in the template
      )
    );
  }

  return {
    defaultAffixedColumnCount: 1,
    allowAddRows: false,
    allowColumnEdits: true,
    allowNoRows: false,
    allowRowRemoval: false,
    columnConstraints: columnDefinitions.map((columnDefinition, i) => ({
      columnDefinition,
      structuredTableColumn: structuredTableColumns[i],
      editLabelDisabled: true,
    })),
    columnLabels: columnDefinitions.map((columnDefinition) => getColumnLabel(columnDefinition)),
    disallowCustomColumns: true,
    legacyFirstEditableColumnIndex: getFirstEditableColumnIndex(columnDefinitions),
    headerRow: false,
    headerCol: false,
    lockedDims: true,
    name,
    rowLabels: [' '],
    table: [
      structuredTableColumns.map((structuredTableColumn, j) => ({
        ...structuredTableColumn.defaultValue,
        ...getConfigLockedCellProperty(columnDefinitions[j]),
      })),
    ],
    widths: structuredTableColumns.map(({width}) => width),
    _tableType: ModelTypeConstants.TRANSFER_TABLE_CONFIG,
    provenanceUuid,
  };
}

export function getTransferTablesInNote(note: Note): Array<TransferTableModel> {
  return getNoteLinkedObjectsOfTypes(note, [ModelTypeConstants.TRANSFER_TABLE]) as Array<TransferTableModel>;
}

export function getTransferTableConfigsInNote(note: Note): Array<TransferTableConfig> {
  return getNoteLinkedObjectsOfTypes(note, [
    ModelTypeConstants.TRANSFER_TABLE_CONFIG,
  ]) as Array<TransferTableConfig>;
}

/**
 * Get the default structured table columns for a transfer table.
 *
 * Keep in sync with get_default_column_specs_for_transfer_table
 * in benchling/workflows/lib/transfer_tables/transfer_tables.py
 */
export function getDefaultStructuredTableColumns({
  containerSchema,
  sourceSampleType,
  parentStorableIsGrid,
  selectorsById,
  fileId,
}: {
  containerSchema: ContainerSchema;
  // Used in tests to override the source sample type
  sourceSampleType?: SourceSampleType;
  parentStorableIsGrid: boolean;
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
  fileId: string;
}): Array<StructuredTableColumn> {
  const baseColumns = _.compact([
    ...getSourceSampleStructuredTableColumns(new Set([sourceSampleType || SourceSampleType.NO_CONTAINER])),
    !APIUtil.isNotebookEntryOrTemplate(fileId) &&
      makeStructuredTableColumn({columnType: TableColumnType.STAGE_RUN, name: 'Stage run'}),
    makeStructuredTableColumn({columnType: TableColumnType.PARENT_STORABLE, name: 'Location'}),
    makeStructuredTableColumn({
      columnType: TableColumnType.NUMBER_OF_CONTAINERS,
      name: 'Number of containers',
    }),
    parentStorableIsGrid &&
      makeStructuredTableColumn({
        columnType: TableColumnType.POSITION,
        name: 'Position',
        columnGroup: TransferTableColumnGroup.TARGET,
      }),
    makeStructuredTableColumn({
      columnType: TableColumnType.CONTAINERS,
      name: 'Containers',
      columnGroup: TransferTableColumnGroup.TARGET,
    }),
    makeStructuredTableColumn({columnType: TableColumnType.QUANTITY, name: 'Quantity'}),
    makeStructuredTableColumn({columnType: TableColumnType.CONCENTRATION, name: 'Concentration'}),
  ]);
  const containerTagColumns = containerSchema.fields.map((schemaField) =>
    makeSchemaFieldStructuredColumn(schemaField, selectorsById)
  );
  return [...baseColumns, ...containerTagColumns];
}

export function structuredTableColumnToColumnDefinition(
  structuredTableColumn: StructuredTableColumn,
  containerSchema: ContainerSchema | null,
  parentStorableIsGrid: boolean,
  stageRuns: Array<StageRun>,
  canChangeUnits: boolean
): ColumnDefinition & {
  // Transfer table StructuredTableColumn objects may have a columnGroup,
  // so we should make sure to include columnGroup in the ColumnDefinition as well
  columnGroup?: ColumnDefinition['columnGroup'];
} {
  if (structuredTableColumn.columnType === TableColumnType.STRING) {
    // Schema field column has been deleted. This could happen when we backfilled Transfer table columns if
    // schema field has been deleted
    const {name, isReadOnly, isArchived, isRequired, columnGroup} = structuredTableColumn;
    return {
      type: TableColumnType.MISSING_FIELD,
      structuredTableColumnName: name,
      locked: isReadOnly,
      isRequired,
      isArchived,
      columnGroup,
    };
  }

  return toColumnDefinition({
    structuredTableColumn,
    stageRuns,
    canChangeUnits,
    schemas: containerSchema != null ? [containerSchema] : [],
    parentStorableIsGrid,
  });
}

/**
 * Parse an imported spreadsheet row into table cells.
 */
export function parseImportedRow(
  columnDefinitions: Array<ColumnDefinition>,
  importedRow: ImportedTransferTableRow
): Array<Cell> {
  const {nonSchemaFieldCellsByColumnType, schemaFieldCellsByFieldId, samplesForSampleDetails} = importedRow;
  const sampleDetails = makeSampleDetailsFromSamples({
    ...samplesForSampleDetails,
    plate: nullableRESTStorableSummaryToGraphQL(samplesForSampleDetails.plate),
  });
  return columnDefinitions.map((columnDefinition, j) => {
    if (columnDefinition.columnGroup === TransferTableColumnGroup.SOURCE) {
      const {text, typedValue} = getSourceColumnCellFromSampleDetails(columnDefinition, sampleDetails);
      return {text, typedValue};
    }
    const spreadsheetColumnType = getSpreadsheetTypeForColumn(
      ModelTypeConstants.CONTAINER_SCHEMA,
      columnDefinition
    );

    let importedTextValue;
    if (spreadsheetColumnType == null) {
      importedTextValue = null;
    } else if (
      spreadsheetColumnType === SpreadsheetBulkImportConstants.COLUMN_TYPE.CONTAINER_SCHEMA_FIELD &&
      columnDefinition.type === TableColumnType.TAG_VALUE
    ) {
      // The function is used for editing, not rendering so its ok to access live schemaField
      importedTextValue = schemaFieldCellsByFieldId[columnDefinition.schemaField.id];
    } else {
      assert(
        columnDefinition.type !== TableColumnType.TAG_VALUE,
        'Unexpected column type mismatch between spreadsheet and transfer table'
      );
      importedTextValue = nonSchemaFieldCellsByColumnType[spreadsheetColumnType];
    }

    return {text: importedTextValue || ''};
  });
}

export function prepareForStore(transferTable: FromServerTransferTableModel, cidIdMap: CidIdMap): void {
  StoreTransformUtil.addCid(transferTable, 'trft', cidIdMap);
  for (const column of transferTable.columns) {
    StoreTransformUtil.addCid(column, 'dtcol', cidIdMap);
  }
}

export function prepareConfigForStore(
  transferTableConfig: FromServerTransferTableConfig,
  cidIdMap: CidIdMap
): void {
  StoreTransformUtil.addCid(transferTableConfig, 'trftcfg', cidIdMap);
  for (const column of transferTableConfig.columns) {
    StoreTransformUtil.addCid(column, 'dtcol', cidIdMap);
  }
}

export function prepareForSave(
  transferTable: TransferTableModel,
  cidIdMap: CidIdMap
): ToServerTransferTableModel {
  return {
    ...transferTable,
    id: cidIdMap[transferTable.cid] as string,
    columns: transferTable.columns.map((column) => prepareLegacyColumnForSave(column, cidIdMap)),
  };
}

export function prepareConfigForSave(
  transferTableConfig: TransferTableConfig,
  cidIdMap: CidIdMap
): ToServerTransferTableConfig {
  return {
    ...transferTableConfig,
    id: cidIdMap[transferTableConfig.cid] as string,
    columns: transferTableConfig.columns.map((column) => prepareLegacyColumnForSave(column, cidIdMap)),
  };
}

export function getOutputSampleIdsForTable(transferOperations: Array<TransferOperation>): Array<string> {
  let stageRunSampleIds: Array<string> = [];
  for (const transferOperation of transferOperations) {
    stageRunSampleIds = [
      ...stageRunSampleIds,
      ...transferOperation.transferRecords.map((record) => record.outputStageRunSampleId),
    ];
  }
  return stageRunSampleIds;
}

export function getEditableRowsForSamples(
  columnConstraints: Array<ColumnConstraint>,
  samplesWithDetails: Array<SampleDetailsWithOptionalStageRunSample>,
  numExistingRows: number
): Cells {
  return samplesWithDetails.map((sampleDetails, i) =>
    columnConstraints.map((columnConstraint, j) =>
      getDefaultCell(
        columnConstraint.columnDefinition!,
        sampleDetails.stageRun,
        sampleDetails,
        columnConstraint.structuredTableColumn!,
        i + numExistingRows,
        j
      )
    )
  );
}

// This function is "all or nothing": if ANY of the required summaries (batch or entity) are
// not available in the provided "byId" mappings, then we return null.
function getSamplesFromRow(
  row: TransferTableRow,
  typedValue: TypedValue,
  targetContainers: StorablesTypedValue | null,
  fileSummariesById: {[id: string]: BioentitySummary},
  batchesById: {[batchId: string]: SampleConstants.Batch}
): Array<SampleConstants.SampleDetailsWithSchema> | null {
  const type = typedValue.type;
  let sample: SampleConstants.SampleDetailsWithSchema;

  if (type === BaseTypedValueType.BIOENTITY) {
    const bioentitySummary = typedValue.bioentitySummary;
    const fileSummary = fileSummariesById[bioentitySummary.id];
    if (!fileSummary) {
      return null;
    }
    sample = {
      ...makeEmptySampleDetails(),
      bioentityInfo: bioentitySummary,
      schema: fileSummary.tagSchema,
    };
  } else {
    assert(type === BaseTypedValueType.BATCH, 'Transfer table samples must be either BIOENTITY or BATCH.');
    const batchInfoSummary = typedValue.batchInfo;
    if (batchInfoSummary.id == null) {
      return null;
    }
    const fullBatchInfo = batchesById[batchInfoSummary.id];
    if (!fullBatchInfo) {
      return null;
    }
    sample = {
      ...makeEmptySampleDetails(),
      batchInfo: batchInfoSummary,
      schema: fullBatchInfo.file.tagSchema,
    };
  }

  if (targetContainers) {
    return targetContainers.storableSummaries.map((container) => ({
      ...sample,
      containerInfo: {
        id: container.id,
        name: container.name,
        barcode: container.barcode,
      },
    }));
  } else {
    return [sample];
  }
}

// To extract samples, we need schemas, which are not part of the cell data. That comes from
// bioentitySummariesById and batchesById, which come from the Flux/Redux store and may not be
// fully loaded. This function will only return a complete data set so we return null if the
// schemas are still loading.
//
export function getTransferredSamples(
  table: TableData,
  rows: Array<TransferTableRow>,
  fileSummariesById: {[id: string]: BioentitySummary},
  batchesById: {[batchId: string]: SampleConstants.Batch}
): Array<SampleConstants.SampleDetailsWithSchema> | null {
  const sampleColumnIndex = getTransferTableColumnDefinitionIndex(
    table,
    TableColumnType.SAMPLE,
    TransferTableColumnGroup.SOURCE,
    true
  );
  const targetContainerColumnIndex = getTransferTableColumnDefinitionIndex(
    table,
    TableColumnType.CONTAINERS,
    TransferTableColumnGroup.TARGET,
    true
  );
  const samples: Array<SampleConstants.SampleDetailsWithSchema> = [];
  for (const row of rows) {
    const sampleTypedValue = row.cells[sampleColumnIndex].typedValue;
    const containerCell = row.cells[targetContainerColumnIndex];
    const targetContainers = containerCell ? (containerCell.typedValue as StorablesTypedValue) : null;
    // We're only interested in finalized (transferred) batches or containable entities
    if (!row.isFinalized) {
      continue;
    }
    assert(
      sampleTypedValue != null &&
        (sampleTypedValue.type === BaseTypedValueType.BIOENTITY ||
          sampleTypedValue.type === BaseTypedValueType.BATCH),
      'Transferred samples must be of type BATCH or BIOENTITY.'
    );

    const rowSamples = getSamplesFromRow(
      row,
      sampleTypedValue,
      targetContainers,
      fileSummariesById,
      batchesById
    );
    if (rowSamples == null) {
      return null; // data are still loading
    }
    samples.push(...rowSamples);
  }
  return samples;
}

export function getTransferTableColumnDefinitionIndex(
  tableData: TableData,
  columnDefinitionType: TableColumnType,
  columnGroup: ColumnGroup | undefined,
  isOptional: boolean
): number {
  return getColumnDefinitionIndex({
    columnDefinitionType,
    tableColumnDefinitions: tableData.columnConstraints.map((constraint) => constraint!.columnDefinition!),
    columnGroup,
    isOptional,
  });
}

function isColumnTypeInColumnInfos(
  columnType: TableColumnType,
  columnInfos: Array<InsertableColumnInfo>
): boolean {
  for (const columnInfo of columnInfos) {
    if (columnType === columnInfo.columnConstraints!.columnDefinition!.type) {
      return true;
    }
  }
  return false;
}

/**
 * Figure out where to insert a source sample column.
 * @param columnTypeToAdd The type of source column to add
 * @param table The table the column will be inserted into
 * @returns The column index to insert after
 */
export function getIndexToInsertSourceColumnAfter(
  columnTypeToAdd: TableColumnType,
  table: TableData
): number {
  let indexToInsertAfter: number;
  switch (columnTypeToAdd) {
    case TableColumnType.CONTAINERS:
      indexToInsertAfter = getTransferTableColumnDefinitionIndex(
        table,
        TableColumnType.SAMPLE,
        TransferTableColumnGroup.SOURCE,
        true
      );
      break;
    case TableColumnType.PLATE:
      indexToInsertAfter = Math.max(
        getTransferTableColumnDefinitionIndex(
          table,
          TableColumnType.SAMPLE,
          TransferTableColumnGroup.SOURCE,
          true
        ),
        getTransferTableColumnDefinitionIndex(
          table,
          TableColumnType.CONTAINERS,
          TransferTableColumnGroup.SOURCE,
          true
        )
      );
      break;
    case TableColumnType.POSITION: {
      // POSITION should always be after PLATE in columnInfosToAdd,
      // so it's safe to only search for a PLATE column
      indexToInsertAfter = getTransferTableColumnDefinitionIndex(
        table,
        TableColumnType.PLATE,
        TransferTableColumnGroup.SOURCE,
        true
      );
      break;
    }
    default:
      throw new Error(`Unexpected column definition type ${columnTypeToAdd}`);
  }

  assert(indexToInsertAfter !== -1, `Could not find a column index to insert a ${columnTypeToAdd} column`, {
    columnConstraints: table.columnConstraints,
  });

  return indexToInsertAfter;
}

export function replaceSourceColumns(
  table: Table,
  columnInfosToAdd: Array<InsertableColumnInfo>
): TableOpOutput {
  const columnTypesToRemove = [
    TableColumnType.CONTAINERS,
    TableColumnType.PLATE,
    TableColumnType.POSITION,
  ].filter(
    // If a column type will be in the new table, don't remove it
    (columnType) => !isColumnTypeInColumnInfos(columnType, columnInfosToAdd)
  );
  let updatedTable = table;
  const tokenUpdaters: Array<TokenUpdater> = [];
  for (const columnTypeToRemove of columnTypesToRemove) {
    const columnIndexToRemove = getTransferTableColumnDefinitionIndex(
      updatedTable,
      columnTypeToRemove,
      TransferTableColumnGroup.SOURCE,
      true
    );
    if (columnIndexToRemove !== -1) {
      const opOutput = excess.ops.deleteColumns(updatedTable, null, columnIndexToRemove, columnIndexToRemove);
      updatedTable = opOutput.table;
      tokenUpdaters.push(...opOutput.tokenUpdaters);
    }
  }

  for (const columnInfo of columnInfosToAdd) {
    const columnTypeToAdd = columnInfo.columnConstraints!.columnDefinition!.type;
    const existingColumnIndex = getTransferTableColumnDefinitionIndex(
      updatedTable,
      columnTypeToAdd,
      TransferTableColumnGroup.SOURCE,
      true
    );
    if (existingColumnIndex === -1) {
      const indexToInsertAfter = getIndexToInsertSourceColumnAfter(columnTypeToAdd, updatedTable);
      const opOutput = excess.ops.insertColumnsRight(
        updatedTable,
        null,
        indexToInsertAfter,
        indexToInsertAfter,
        columnInfo
      );
      updatedTable = opOutput.table;
      tokenUpdaters.push(...opOutput.tokenUpdaters);
    }
  }

  return {
    table: updatedTable,
    tokenUpdaters,
    box: null,
    dim: TableDimension.COLUMN,
    tableOp: {[table.name]: {type: 'eval-table'}},
  };
}

export function getLockedCellProperty(columnDefinition: ColumnDefinition): Partial<Cell> {
  switch (columnDefinition.type) {
    case TableColumnType.STAGE_RUN:
      return {
        locked: true,
      };
    case TableColumnType.SAMPLE:
    case TableColumnType.PLATE:
    case TableColumnType.POSITION:
    case TableColumnType.CONTAINERS:
      if (columnDefinition.columnGroup === TransferTableColumnGroup.SOURCE) {
        return {
          locked: true,
        };
      } else {
        return {};
      }
    default: {
      return {};
    }
  }
}

export function getFirstEditableColumnIndex(columnDefinitions: Array<ColumnDefinition>): number {
  const firstRemovableColumnIndex = columnDefinitions.findIndex((columnDefinition) =>
    [TableColumnType.QUANTITY, TableColumnType.CONCENTRATION, TableColumnType.TAG_VALUE].includes(
      columnDefinition.type
    )
  );
  if (firstRemovableColumnIndex === -1) {
    return columnDefinitions.length;
  }
  return firstRemovableColumnIndex;
}
